import { X } from "@phosphor-icons/react";
import { fmtDate, fmtMoney, vehicleLabel } from "@/lib/format";

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
        {label}
      </span>
      <span className={`text-sm text-ink ${mono ? "font-mono" : "font-medium"}`}>
        {value || "—"}
      </span>
    </div>
  );
}

function PackagesLines({ packages }) {
  if (!packages?.length) return null;
  return (
    <div className="border-t border-canvas-line pt-3 space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
        Packages
      </div>
      {packages.map((p) => {
        const finalPrice = p.final_price ?? p.price;
        const included = p.items_included || [];
        const excluded = p.items_excluded || [];
        // Legacy snapshots (pre-item-pricing) had no items_included; treat discount field too.
        const hasItemBreakdown = included.length > 0 || excluded.length > 0;
        const hasDiscount = p.discount && p.discount > 0;
        return (
          <div key={p.id} className="text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="text-ink font-medium truncate pr-2">{p.name}</span>
              <span className="font-mono shrink-0">
                {hasDiscount && (
                  <span className="line-through text-ink-subtle mr-1">{fmtMoney(p.price)}</span>
                )}
                {fmtMoney(finalPrice)}
              </span>
            </div>
            {hasItemBreakdown && (
              <ul className="mt-1 pl-3 space-y-0.5">
                {included.map((it, idx) => (
                  <li
                    key={`inc-${it.label}-${idx}`}
                    className="flex items-center justify-between text-[11px] text-ink-muted"
                  >
                    <span className="truncate pr-2">{it.label}</span>
                    <span className="font-mono shrink-0">{fmtMoney(it.price)}</span>
                  </li>
                ))}
                {excluded.map((it, idx) => (
                  <li
                    key={`exc-${it.label}-${idx}`}
                    className="flex items-center justify-between text-[11px] text-ink-subtle line-through"
                  >
                    <span className="truncate pr-2">{it.label} (excluded)</span>
                    <span className="font-mono shrink-0">{fmtMoney(it.price)}</span>
                  </li>
                ))}
              </ul>
            )}
            {hasDiscount && (
              <div className="text-[10px] font-mono text-[#FF8C00] mt-0.5">
                − {fmtMoney(p.discount)} discount
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function InvoiceModal({ invoice, onClose }) {
  if (!invoice) return null;
  const vehicleLabelStr = invoice.vehicle ? vehicleLabel(invoice.vehicle) : "—";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} />
      <div
        data-testid="invoice-modal"
        className="relative bg-canvas-card border border-ink w-full max-w-md"
      >
        <div className="px-6 py-5 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted">
              Invoice / Sale Confirmed
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">
              #{invoice.id.slice(0, 8).toUpperCase()}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 border border-canvas-line hover:border-ink"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <Row label="Date" value={fmtDate(invoice.sale_date)} />
          <Row label="Vehicle" value={vehicleLabelStr} />
          <Row label="VIN" value={invoice.vehicle?.vin} mono />
          <Row label="Customer" value={invoice.customer?.name} />
          <Row label="Email" value={invoice.customer?.email} mono />
          <Row label="Payment" value={invoice.payment_method.toUpperCase()} mono />
          <PackagesLines packages={invoice.packages} />
          <div className="border-t border-canvas-line pt-3 flex items-end justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              Total
            </span>
            <span className="font-display text-3xl font-bold text-ink">
              {fmtMoney(invoice.sale_price)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
